import styled from "styled-components";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";
import {
  BORDER_COLORS,
  TEXT_COLORS,
  BACKGROUND_COLORS,
  COLORS,
} from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { FONT_WEIGHT } from "../../constants/design/fontWeight";

export const PnLWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: calc(100% - ${COMPONENTS.header.main}px);
  padding: ${SPACING.six}px;
  overflow-y: auto;
`;

export const ContentContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${SPACING.four}px;
`;

export const PageTitle = styled.h1`
  color: ${TEXT_COLORS.one};
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

export const FiltersWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${SPACING.four}px;
  margin-bottom: ${SPACING.four}px;
  flex-wrap: wrap;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;

  span {
    color: ${TEXT_COLORS.two};
    font-size: 14px;
    font-weight: ${FONT_WEIGHT.medium};
  }
`;

export const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  cursor: pointer;
  user-select: none;
  color: ${TEXT_COLORS.two};
  font-size: 14px;
  font-weight: ${FONT_WEIGHT.medium};
  transition: color 0.2s ease;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;

export const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${COLORS.blue.one};
  margin: 0;
`;

export const StatsCardsWrapper = styled.div<{ isAllAssets?: boolean }>`
  display: flex;
  gap: ${SPACING.four}px;
  margin-bottom: ${SPACING.four}px;
  width: 100%;
  overflow-x: auto;

  /* Hide scrollbar for cleaner look but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: ${BORDER_COLORS.two} transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${BORDER_COLORS.two};
    border-radius: 3px;
  }
`;

export const StatCard = styled.div<{ isLoading?: boolean }>`
  border: 1px solid ${BORDER_COLORS.two};
  background: ${BACKGROUND_COLORS.four};
  border-radius: 8px;
  padding: ${SPACING.four}px ${SPACING.five}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
  min-height: 100px;
  min-width: 200px;
  flex: 1;
  position: relative;
  overflow: hidden;

  ${({ isLoading }) =>
    isLoading &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.05),
        transparent
      );
      animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
      100% {
        left: 100%;
      }
    }
  `}
`;

export const StatCardLabel = styled.div`
  color: ${TEXT_COLORS.three};
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const getStatCardOpacity = (isLoading?: boolean, isEmpty?: boolean): number => {
  if (isLoading) return 0.4;
  if (isEmpty) return 0.6;
  return 1;
};

export const StatCardValue = styled.div<{ isLoading?: boolean; isEmpty?: boolean }>`
  color: ${({ isEmpty }) => (isEmpty ? TEXT_COLORS.three : TEXT_COLORS.one)};
  font-size: ${FONT_SIZE.four};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.2;
  opacity: ${({ isLoading, isEmpty }) => getStatCardOpacity(isLoading, isEmpty)};
  transition: opacity 0.2s ease;
`;

export const TabsWrapper = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  margin-bottom: ${SPACING.four}px;
  border-bottom: 1px solid ${BORDER_COLORS.two};
`;

const getTabBorderColor = (isActive: boolean): string => isActive ? COLORS.blue.one : "transparent";

const getTabTextColor = (isActive: boolean): string => isActive ? COLORS.blue.one : TEXT_COLORS.two;

const getTabFontWeight = (isActive: boolean): number => isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.medium;

export const Tab = styled.button<{ isActive: boolean }>`
  padding: ${SPACING.two}px ${SPACING.four}px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ isActive }) => getTabBorderColor(isActive)};
  color: ${({ isActive }) => getTabTextColor(isActive)};
  font-size: ${FONT_SIZE.two};
  font-weight: ${({ isActive }) => getTabFontWeight(isActive)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${COLORS.blue.one};
  }
`;

export const DateInput = styled.input`
  height: 32px;
  background: ${BACKGROUND_COLORS.component};
  border: 1px solid ${BORDER_COLORS.one};
  color: ${TEXT_COLORS.one};
  padding: ${SPACING.two}px;
  border-radius: 6px;
  font-size: ${FONT_SIZE.two};

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

export const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  margin-top: ${SPACING.two}px;
  width: 100%;
`;
